import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { getActiveStudent, selectProduct } from '@actions/checkout'
import { openModal, closeModal } from '@actions/modal'
import KnowTheProject from '@components/KnowTheProject'
import VoucherForm from '@components/VoucherForm'
import StudentForm from '@components/VoucherForm/StudentForm'
import { educaLabelOf } from '@utils/product/ecosLabel'

import V2Layout from '../V2Layout'
import V2CartAside from '../V2CartAside'
import V2Stepper from '../V2Stepper'
import V2CoverStack from '../V2CoverStack'

// Seleção de produtos V2 — porte fiel do design "Nova home SM Educação".
// Regras preservadas (MATRIZ): P-01/02 (linhas e ordem), P-05/06 (itens obrigatórios),
// P-07 (confirmação de remoção — inline, mesmos textos, conforme MAPA_ALERTAS),
// P-19/20/21 (cascata e exibição de desconto), P-24..P-28 (pack Educamos/Ecos), P-30.
// O alerta de prazo de entrega (P-08..P-16) foi centralizado na etapa de Resumo (MAPA_ALERTAS).

const BUSINESS_LINES = [
  { id: 1, title: 'Livros didáticos' },
  { id: 3, title: 'Livros de literatura' },
  { id: 2, title: 'Soluções Educacionais' },
  { id: 8, title: 'Livros digitais' },
  { id: 7, title: 'Serviços' },
  { id: 9, title: 'Bilíngue' },
  { id: 13, title: 'Religioso' },
]
const EDUCA_ID = 2
const BILINGUAL_ID = 9

// mesmo formato do Resumo e do carrinho (separador de milhar) — ajuste de 09/09
const money = v => Number(v || 0).toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })


export default function V2SelectProducts() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const student = useSelector(state => getActiveStudent(state))
  const voucherForm = useSelector(state => state.voucherForm || {})
  const [confirmingSap, setConfirmingSap] = useState(null)

  sessionStorage.setItem('smd_v2_flow', '1')

  const products = (student && student.products) || []
  const isTeacher = student && student.voucher && student.voucher.toLowerCase().startsWith('p')

  // P-19: cascata de desconto (professor → especial → custom → padrão)
  const discountOf = p => {
    if (isTeacher && p.custom_discount_teacher) return p.custom_discount_teacher
    if (student && Number.isInteger(student.special_discount_id)) return student.discount_value
    if (p.has_custom_discount) return p.custom_discount
    return p.discount
  }

  // P-05/P-06: item obrigatório (não removível ou entrega imediata)
  const isLocked = p =>
    !p.can_remove || String(p.educa_delivery_type) === '7' || student.immediate_delivery === 1

  // 28/09 (testes de negócio): com Sistema de Ensino (Ecos/Educamos) na lista a instrução de
  // desmarcar não aparece, mesmo havendo outras linhas; sem ele, só quando há item removível
  const hasEduca = products.some(p => p.business_line_id === EDUCA_ID)
  const hasOptional = !hasEduca && products.some(p => !isLocked(p))

  const _toggle = p => {
    if (isLocked(p)) return
    if (p.active) {
      // P-07: remoção exige confirmação (inline no card, mesmos textos)
      setConfirmingSap(p.product_sap_code)
    } else {
      dispatch(selectProduct(student, p, true))
    }
  }

  const _confirmRemove = p => {
    dispatch(selectProduct(student, p, false))
    setConfirmingSap(null)
  }

  const lines = BUSINESS_LINES.map(line => ({
    ...line,
    items: products.filter(p => p.business_line_id === line.id),
  })).filter(line => line.items.length > 0)

  // P-27: rótulo do pack Educamos
  const educaItems = products.filter(p => p.business_line_id === EDUCA_ID)
  const educaLabel = educaLabelOf(educaItems)


  // Linha de produto — padrão de lista do guia de identidade (miniatura, nome, de/por,
  // caixa de seleção). Regras intactas: P-05/06 (obrigatório), P-07 (confirmação inline
  // com os mesmos textos), P-19..21 (cascata e exibição do desconto).
  const renderCard = p => {
    const locked = isLocked(p)
    const selected = !!p.active
    const pct = discountOf(p)
    const showFrom = p.amount && p.amount_discounted && (p.amount !== p.amount_discounted || (isTeacher && p.custom_discount_teacher))
    const confirming = confirmingSap === p.product_sap_code
    const cover = p.images && p.images.GraficoBig ? p.images.GraficoBig : null

    return (
      <article
        key={p.product_sap_code}
        className={`v2-prow${selected ? ' is-on' : ''}${locked ? ' is-locked' : ''}`}
        onClick={() => !confirming && _toggle(p)}
        // acessibilidade (guia, item 9): a linha é uma caixa de seleção alcançável por teclado
        role="checkbox"
        aria-checked={selected}
        aria-disabled={locked || undefined}
        aria-label={p.name}
        tabIndex={locked ? -1 : 0}
        onKeyDown={e => {
          if ((e.key === ' ' || e.key === 'Enter') && e.target === e.currentTarget && !confirming) {
            e.preventDefault()
            _toggle(p)
          }
        }}
      >
        <div className="v2-prow__main">
          <span className="v2-prow__thumb">
            {cover ? <img src={cover} alt="" /> : <span>capa</span>}
          </span>
          <div className="v2-prow__body">
            <h3>{p.name}</h3>
            <span className="v2-prow__meta">
              {showFrom && pct ? <em>{pct}% de desconto</em> : null}
            </span>
          </div>
          <div className="v2-prow__price">
            {showFrom && <s>de {money(p.amount)}</s>}
            <strong>{money(p.amount_discounted)}</strong>
          </div>
          <span className="v2-check" aria-hidden="true">{selected ? '✓' : ''}</span>
        </div>

        {confirming && (
          <div className="v2-prow__confirm" onClick={e => e.stopPropagation()}>
            <div>
              <strong>Você está deixando de comprar o livro abaixo:</strong>
              <span>{p.name}</span>
              <strong>Está certo disso?</strong>
            </div>
            <div className="v2-prow__confirm-actions">
              <button type="button" className="v2-btn v2-btn--primary v2-btn--sm" onClick={() => _confirmRemove(p)}>
                Continuar
              </button>
              <button type="button" className="v2-btn v2-btn--neutral v2-btn--sm" onClick={() => setConfirmingSap(null)}>
                Cancelar
              </button>
            </div>
          </div>
        )}
      </article>
    )
  }

  // P-24..P-28: Soluções Educacionais como pacote único, sempre selecionado e bloqueado;
  // capas em leque como no V1, sem o rótulo "Kit obrigatório" (ajustes de negócio 09/09)
  const renderEducaPack = items => {
    const total = items.reduce((s, p) => s + Number(p.amount_discounted || 0), 0)
    return (
      <article id="v2-educa-pack" className="v2-prow is-on is-locked v2-prow--pack">
        <div className="v2-prow__main">
          <div className="v2-prow__stack">
            <V2CoverStack items={items} size={84} max={8} align="flex-start" />
          </div>
          <div className="v2-prow__body">
            <h3>
              {educaLabel} — {student.study_year} · Livro do Aluno
            </h3>
            <span className="v2-prow__meta">{items.length} itens no kit</span>
          </div>
          <div className="v2-prow__price">
            <strong>Por: {money(total)}</strong>
          </div>
          <span className="v2-check" aria-hidden="true">✓</span>
        </div>
      </article>
    )
  }

  // V-45: com o formulário de novo aluno/edição ativo, ele substitui o conteúdo do passo
  if (voucherForm.show || voucherForm.showStudentForm) {
    return (
      <V2Layout>
        <V2Stepper current={1} />
        <div className="v2-uuf v2-form-wrap" style={{ maxWidth: '760px', margin: '0 auto', padding: '48px 32px 110px' }}>
          {voucherForm.show ? <VoucherForm /> : <StudentForm />}
        </div>
      </V2Layout>
    )
  }

  return (
    <V2Layout>
      <V2Stepper current={1} />
      <div className="v2-step-grid" style={{ maxWidth: '1200px', margin: '0 auto', padding: '56px 32px 110px', display: 'grid', gridTemplateColumns: '1fr 340px', gap: '40px', alignItems: 'start' }}>
        <div>
        <span className="v2-eyebrow">Etapa 2 de 5</span>
        <h1 className="v2-step-title">A lista da escola já está montada</h1>
        {student && student.school_name && (
          <p className="v2-step-lede">
            Esta é a lista indicada para o{' '}
            <strong>
              {student.study_year} do {student.study_step}
            </strong>{' '}
            {/* rodada 3 (09/09, item 19): nomes cadastrados com ponto final geravam ".." */}
            no {String(student.school_name).replace(/[.\s]+$/, '')}.
            {/* pedido de negócio (19/09 e 28/09): sem a instrução quando há Sistema de Ensino
                ou quando nada pode ser desmarcado (Bilíngue/UDP é sempre obrigatório) */}
            {hasOptional ? ' Desmarque o que você não quiser comprar.' : ''}
          </p>
        )}

        {lines.map(line => (
          <section key={line.id} id={`v2-line-${line.id}`} className="v2-plist">
            <header className="v2-plist__head">
              {/* pedido de negócio (09/09, item 4): a seção se chama "Sistema de Ensino" */}
              <h2>{line.id === EDUCA_ID ? 'Sistema de Ensino' : line.title}</h2>
              {/* pedido de negócio (09/09, rodada 2, item 12): mesmo modal do V1 (KnowTheProject) com o skin V2 */}
              {line.id === BILINGUAL_ID && (
                <button
                  id="v2-know-project"
                  type="button"
                  className="v2-chipbtn"
                  onClick={() =>
                    dispatch(
                      openModal(
                        <div className="v2-ktp">
                          <KnowTheProject closeModal={() => dispatch(closeModal())} />
                        </div>
                      )
                    )
                  }
                >
                  Conheça o projeto
                </button>
              )}
              <span className="v2-plist__count">
                {line.items.filter(p => p.active).length} de {line.items.length} selecionados
              </span>
            </header>
            {line.id === EDUCA_ID ? renderEducaPack(line.items) : line.items.map(renderCard)}
          </section>
        ))}
        </div>
        <V2CartAside
          cta={
            <button id="v2-products-continue" type="button" className="v2-btn v2-btn--primary v2-btn--block" onClick={() => navigate('/v2/steps/identificacao')}>
              Continuar
            </button>
          }
        />
      </div>
    </V2Layout>
  )
}
